import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './cn.js';

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
  bodyClassName,
  headerClassName,
  footerClassName,
  fullScreen = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  /// As ações do diálogo. Ficam aqui, e não no fim do conteúdo, para não rolarem junto com ele:
  /// numa lista longa isso obriga a percorrer tudo antes de poder confirmar.
  footer?: ReactNode;
  className?: string;
  /// Para quem administra a própria rolagem por dentro do corpo, como lista e detalhe lado a lado.
  bodyClassName?: string;
  headerClassName?: string;
  footerClassName?: string;
  /// Ocupa a tela inteira, sem margem nem borda arredondada: para quando a área útil é o que
  /// importa, como assinar com o dedo no celular.
  fullScreen?: boolean;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-50 grid overflow-y-auto overscroll-contain bg-black/75 backdrop-blur-sm data-[state=open]:animate-[fade-in_.14s_ease-out]',
            fullScreen ? 'p-0' : 'p-3 sm:p-6',
          )}
        >
          <Dialog.Content
            className={cn(
              'relative m-auto flex w-full flex-col overflow-hidden bg-surface-card shadow-[0_24px_80px_rgb(0_0_0/.65)] outline-none data-[state=open]:animate-[modal-in_.16s_ease-out]',
              fullScreen
                ? 'h-dvh max-w-none'
                : 'max-h-[calc(100dvh-1.5rem)] max-w-2xl rounded-2xl border border-surface-border sm:max-h-[calc(100dvh-3rem)]',
              className,
            )}
          >
            {/* O cabeçalho fica parado enquanto o corpo rola: em formulário longo o Fechar
                não pode sumir para cima. */}
            <div
              className={cn(
                'flex shrink-0 items-start gap-4 border-b border-surface-border px-4 py-4 sm:px-7 sm:py-5',
                headerClassName,
              )}
            >
              <div className="min-w-0 flex-1">
                <Dialog.Title className="text-xl font-semibold text-content">{title}</Dialog.Title>
                {description ? (
                  <Dialog.Description className="mt-1.5 text-sm leading-6 text-content-muted">
                    {description}
                  </Dialog.Description>
                ) : null}
              </div>
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label="Fechar"
                  className="grid size-10 shrink-0 place-items-center rounded-xl border border-surface-border text-content-muted transition hover:border-brand-500/60 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                >
                  <X className="size-5" aria-hidden="true" />
                </button>
              </Dialog.Close>
            </div>
            <div
              className={cn(
                'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-7 sm:py-6',
                bodyClassName,
              )}
            >
              {children}
            </div>
            {footer ? (
              <div
                className={cn(
                  'flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-surface-border px-4 py-4 sm:px-7',
                  footerClassName,
                )}
              >
                {footer}
              </div>
            ) : null}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
